import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {FormBuilder, UntypedFormGroup} from "@angular/forms";
import {ISupervisor} from "../../model/supervisor.model";
import {IOrganization} from "../../model/organization.model";
import {SupervisorService} from "../../../core/service/supervisor.service";
import {OrganizationService} from "../../../core/service/organization.service";
import {debounceTime, distinctUntilChanged, forkJoin, from, map, mergeMap, of, switchMap, take, toArray} from "rxjs";
import {ISupervisorSearchFilter} from "../../model/supervisor-search-filter.model";
import {Paging} from "../../constants/paging.constants";
import {ServiceStatus} from "../../model/service-registration.model";
import {SharedPrimitivesModule} from "../../shared-primitives.module";
import {IconComponent} from "../../icon/icon.component";

@Component({
  selector: 'app-supervisor-search',
  templateUrl: './supervisor-search.component.html',
  styleUrl: './supervisor-search.component.scss',
  standalone: true,
  imports: [
    SharedPrimitivesModule,
    IconComponent
  ]
})
export class SupervisorSearchComponent implements OnInit, OnChanges {
  @Output() searchResults = new EventEmitter<any[]>();

  @Input() keywords: string = '';
  @Output() keywordsChange = new EventEmitter<string>();

  @Input() enableInactive: boolean = true;
  @Input() showInactive: boolean = false;
  @Output() showInactiveChange = new EventEmitter<boolean>();

  public form: UntypedFormGroup = new UntypedFormGroup({});
  public supervisors: ISupervisor[] = [];
  public orgList: IOrganization[] = [];
  public numVols: number[] = [];

  constructor(
    private fb: FormBuilder,
    private supervisorService: SupervisorService,
    private organizationService: OrganizationService
  ) {
  }

  ngOnInit() {
    this.initForm();
    this.setupFormListeners();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['initialKeywords'] || changes['initialShowInactive']) {
      this.form.patchValue({
        keywords: this.keywords,
        showInactive: this.showInactive
      })
    }
  }

  private initForm() {
    this.form = this.fb.group({
      keywords: [this.keywords],
      showInactive: [this.showInactive]
    });
  }

  private setupFormListeners() {
    this.form.get('keywords')?.valueChanges.pipe(
      debounceTime(1000),
      distinctUntilChanged()
    ).subscribe((value) => {
      this.keywordsChange.emit(value);
      this.performSearch();
    });

    this.form.get('showInactive')?.valueChanges.pipe(
      distinctUntilChanged()
    ).subscribe((value) => {
      this.showInactiveChange.emit(value);
      this.performSearch();
    });
  }

  public performSearch() {
    if (this.form.get('keywords')?.value) {
      const searchFilter: ISupervisorSearchFilter = {
        text: this.form.get('keywords')?.value,
        status: this.form.get('showInactive')?.value,
        limit: Paging.LARGE_SEARCH_RESULT_PAGE_SIZE.toString(),
        offset: '0',
      };
      this.orgList = [];
      this.numVols = [];
      this.searchFor(searchFilter);
    }
  }

  private searchFor(searchFilter: ISupervisorSearchFilter) {
    this.supervisorService.findSupervisors(searchFilter).pipe(
      switchMap(supervisors => from(supervisors.data)),
      mergeMap(supervisor => forkJoin({
        sup: of(supervisor),
        orgs: this.getOrganizationsFromSupervisorId(supervisor.id),
        numVol: this.getVolunteersFromSupervisorId(supervisor.id)
      })),
      toArray()
    ).subscribe(results => this.searchResults.emit(results));
  }

  private getOrganizationsFromSupervisorId(id: string) {
    return this.organizationService.getOrganizationsFromAll(id).pipe(take(1));
  }

  private getVolunteersFromSupervisorId(id: string) {
    return this.supervisorService.getVolunteers(id).pipe(
      map(services => services.filter(service => service.status === ServiceStatus.REGISTERED).length)
    );
  }

}
